import React, { useState } from 'react';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import { Button, KIND } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Textarea } from 'baseui/textarea';
import { IDENTITY_TYPES, LIMITS } from '../constants';
import { IdentifyForm as IdentifyFormValues, IdentifyFormErrors, IdentityType } from '../types';
import useStyles from '../useStyles';
import { validateIdentifyForm } from '../utils/validation';

const FIELD_IDS: Record<keyof IdentifyFormValues, string> = {
	ticketId: 'dpdpa-ticket-id',
	reason: 'dpdpa-reason',
	identityType: 'dpdpa-identity-type',
	value: 'dpdpa-identity-value'
};

const FIELD_ORDER: Array<keyof IdentifyFormValues> = ['ticketId', 'reason', 'identityType', 'value'];

const errorId = (field: keyof IdentifyFormValues): string => `${FIELD_IDS[field]}-error`;

const describedBy = (field: keyof IdentifyFormValues, error?: string) => ({
	Input: {
		props: {
			'aria-describedby': error ? errorId(field) : undefined,
			'aria-invalid': error ? 'true' : undefined
		}
	}
});

interface IIdentifyFormProps {
	values: IdentifyFormValues;
	onChange: (values: IdentifyFormValues) => void;
	onSubmit: (values: IdentifyFormValues) => void;
	isLoading: boolean;
}

const IdentifyForm: React.FC<IIdentifyFormProps> = ({ values, onChange, onSubmit, isLoading }) => {
	const { classes } = useStyles();
	const [touched, setTouched] = useState<Partial<Record<keyof IdentifyFormValues, boolean>>>({});
	const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);

	const errors: IdentifyFormErrors = validateIdentifyForm(values);
	const visibleError = (field: keyof IdentifyFormValues): string | undefined =>
		submitAttempted || touched[field] ? errors[field] : undefined;

	const setField = (field: keyof IdentifyFormValues, fieldValue: string) => onChange({ ...values, [field]: fieldValue });
	const markTouched = (field: keyof IdentifyFormValues) => setTouched((prev) => ({ ...prev, [field]: true }));

	const selectedType = _find(IDENTITY_TYPES, { id: values.identityType }) || IDENTITY_TYPES[0];

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (isLoading) return;
		setSubmitAttempted(true);
		if (!_isEmpty(errors)) {
			const firstInvalid = _find(FIELD_ORDER, (field) => !!errors[field]);
			if (firstInvalid) document.getElementById(FIELD_IDS[firstInvalid])?.focus();
			return;
		}
		onSubmit(values);
	};

	const renderError = (field: keyof IdentifyFormValues) => {
		const error: string | undefined = visibleError(field);
		return error ? (
			<div id={errorId(field)} className={classes.fieldError}>
				{error}
			</div>
		) : null;
	};

	const reasonLength: number = values.reason.trim().length;

	return (
		<form noValidate onSubmit={handleSubmit} aria-label='Identify the user' className={classes.card}>
			<h3 className={classes.sectionTitle}>Identify the user</h3>
			<div className={classes.formGrid}>
				<div>
					<FormControl label='Legal ticket ID'>
						<Input
							id={FIELD_IDS.ticketId}
							value={values.ticketId}
							onChange={(e) => setField('ticketId', e.currentTarget.value)}
							onBlur={() => markTouched('ticketId')}
							error={!!visibleError('ticketId')}
							placeholder='e.g. LEGAL-1234'
							overrides={describedBy('ticketId', visibleError('ticketId'))}
						/>
					</FormControl>
					{renderError('ticketId')}
				</div>
				<div>
					<FormControl label='Identifier type'>
						<Select
							id={FIELD_IDS.identityType}
							options={IDENTITY_TYPES}
							value={[selectedType]}
							labelKey='label'
							valueKey='id'
							clearable={false}
							searchable={false}
							onChange={({ value }) => {
								const nextType = value[0]?.id as IdentityType | undefined;
								if (nextType) onChange({ ...values, identityType: nextType });
							}}
						/>
					</FormControl>
				</div>
			</div>
			<FormControl label='Reason for access'>
				<Textarea
					id={FIELD_IDS.reason}
					value={values.reason}
					onChange={(e) => setField('reason', e.currentTarget.value)}
					onBlur={() => markTouched('reason')}
					error={!!visibleError('reason')}
					placeholder='Why is this data being retrieved? e.g. Data principal access request under DPDP Act s.11'
					overrides={describedBy('reason', visibleError('reason'))}
				/>
			</FormControl>
			{renderError('reason') || (
				<div className={classes.fieldHint} aria-hidden='true'>
					{`${reasonLength} / ${LIMITS.REASON_MAX} characters (minimum ${LIMITS.REASON_MIN})`}
				</div>
			)}
			<FormControl label={`${selectedType.label} value`}>
				<Input
					id={FIELD_IDS.value}
					value={values.value}
					onChange={(e) => setField('value', e.currentTarget.value)}
					onBlur={() => markTouched('value')}
					error={!!visibleError('value')}
					placeholder={selectedType.placeholder}
					overrides={describedBy('value', visibleError('value'))}
				/>
			</FormControl>
			{renderError('value')}
			<div className={classes.actionsRow}>
				<Button type='submit' kind={KIND.primary} isLoading={isLoading} disabled={isLoading}>
					Find account
				</Button>
				<span className={classes.muted}>Searches, views and exports are logged for audit against this ticket.</span>
			</div>
		</form>
	);
};

export default IdentifyForm;
